import { createAsyncThunk } from '@reduxjs/toolkit';

// Services
import { CounsellorsService } from '../../services/counsellors.service';

// Store entities
import { openNotification } from '../app-notifications/app-notifications.thunks';
import { updateCurrentCounsellorInfo } from '../current-counsellor/current-counsellor.thunks';

// Slice
import { CounsellorsSliceActions } from './counsellors.slice';

// Models
import { ICounsellor, ICounsellorCreate, ICounsellorsFilters } from '../../interfaces/counsellors.interfaces';

export enum ECounsellorsThunks {
  LoadCounsellors = 'COUNSELLORS/loadCounsellors',
  CreateCounsellor = 'COUNSELLORS/createCounsellor',
  UpdateCounsellor = 'COUNSELLORS/updateCounsellor',
  SaveCounsellorsFilters = 'COUNSELLORS/saveCounsellorsFilters',
  ClearCounsellors = 'COUNSELLORS/clearCounsellors',
  ActivateCounsellor = 'COUNSELLORS/activateCounsellor',
  DeactivateCounsellor = 'COUNSELLORS/deactivateCounsellor',
  DeleteCounsellor = 'COUNSELLORS/deleteCounsellor',
  ArchiveCounsellor = 'COUNSELLORS/archiveCounsellor',
  UnblockCounsellor = 'COUNSELLORS/unblockCounsellor',
  BlockCounsellor = 'COUNSELLORS/blockCounsellor',
  ShareLinkCounsellor = 'COUNSELLORS/shareLinkCounsellor',
  ResendInvitationCounsellor = 'COUNSELLORS/resendInvitationCounsellor',
}

export const loadCounsellors = createAsyncThunk(
  ECounsellorsThunks.LoadCounsellors,
  async ({ filters, isFirstLoad = false }: {
    filters: ICounsellorsFilters,
    isFirstLoad?: boolean
  }, { dispatch }) => {
    dispatch(CounsellorsSliceActions.loadCounsellorsRequest({ isFirstLoad }));
    const { data, error } = await CounsellorsService.fetchCounsellors(filters);
    if (!data) {
      return dispatch(CounsellorsSliceActions.loadCounsellorsRequestFailure(error ?? 'Error'));
    }
    return dispatch(CounsellorsSliceActions.loadCounsellorsRequestSuccess(data));
  }
);

export const createCounsellor = createAsyncThunk(
  ECounsellorsThunks.CreateCounsellor,
  async ({ counsellor }: { counsellor: ICounsellorCreate, }, { dispatch }) => {
    const { experts, error } = await CounsellorsService.createNewCounsellor(counsellor);
    if (!experts) {
      dispatch(openNotification({
        type: 'error',
        description: error ?? 'The Consultant could not be created, try again'
      }));
      return false;
    }
    dispatch(openNotification({
      type: 'success',
      description: 'Consultant added successfully! Go to their Profile to access all settings.'
    }));
    return experts;
  }
);

export const updateCounsellor = createAsyncThunk(
  ECounsellorsThunks.UpdateCounsellor,
  async ({ counsellor }: { counsellor: ICounsellor, }, { dispatch }) => {
    dispatch(CounsellorsSliceActions.updateCounsellorInfo(counsellor));
    return counsellor;
  }
);

export const activateCounsellor = createAsyncThunk(
  ECounsellorsThunks.ActivateCounsellor,
  async ({ counsellor_id }: { counsellor_id: number }, { dispatch }) => {
    const { worker, error } = await CounsellorsService.activateCounsellor(counsellor_id);
    if (!worker) {
      dispatch(openNotification({
        type: 'error',
        description: error ?? 'The Consultant could not be activated, try again'
      }));
      return false;
    }
    dispatch(openNotification({
      type: 'success',
      description: 'The Consultant was activated'
    }));
    dispatch(updateCurrentCounsellorInfo({ counsellor: worker }));
    dispatch(updateCounsellor({ counsellor: worker }));
    return worker;
  }
);

export const deactivateCounsellor = createAsyncThunk(
  ECounsellorsThunks.DeactivateCounsellor,
  async ({ counsellor_id }: { counsellor_id: number }, { dispatch }) => {
    const { worker, error } = await CounsellorsService.deactivateCounsellor(counsellor_id);
    if (!worker) {
      dispatch(openNotification({
        type: 'error',
        description: error ?? 'The Consultant could not be deactivated, try again'
      }));
      return false;
    }
    dispatch(openNotification({
      type: 'success',
      description: 'The Consultant was deactivated'
    }));
    dispatch(updateCurrentCounsellorInfo({ counsellor: worker }));
    dispatch(updateCounsellor({ counsellor: worker }));
    return worker;
  }
);

export const deleteCounsellor = createAsyncThunk(
  ECounsellorsThunks.DeleteCounsellor,
  async ({ counsellor_id }: { counsellor_id: number }, { dispatch }) => {
    const { worker, error } = await CounsellorsService.deleteCounsellor(counsellor_id);
    if (!worker) {
      dispatch(openNotification({
        type: 'error',
        description: error ?? 'The Consultant could not be deleted, try again'
      }));
      return false;
    }
    dispatch(openNotification({
      type: 'success',
      description: 'The Consultant was deleted'
    }));
    dispatch(updateCurrentCounsellorInfo({ counsellor: worker }));
    dispatch(updateCounsellor({ counsellor: worker }));
    return worker;
  }
);

export const blockCounsellor = createAsyncThunk(
  ECounsellorsThunks.BlockCounsellor,
  async ({ counsellor_id }: { counsellor_id: number }, { dispatch }) => {
    const { worker, error } = await CounsellorsService.blockCounsellor(counsellor_id);
    if (!worker) {
      dispatch(openNotification({
        type: 'error',
        description: error ?? 'The Consultant could not be blocked, try again'
      }));
      return false;
    }
    dispatch(openNotification({
      type: 'success',
      description: 'The Consultant was blocked'
    }));
    dispatch(updateCurrentCounsellorInfo({ counsellor: worker }));
    dispatch(updateCounsellor({ counsellor: worker }));
    return worker;
  }
);

export const unblockCounsellor = createAsyncThunk(
  ECounsellorsThunks.UnblockCounsellor,
  async ({ counsellor_id }: { counsellor_id: number }, { dispatch }) => {
    const { worker, error } = await CounsellorsService.unblockCounsellor(counsellor_id);
    if (!worker) {
      dispatch(openNotification({
        type: 'error',
        description: error ?? 'The Consultant could not be unblocked, try again'
      }));
      return false;
    }
    dispatch(openNotification({
      type: 'success',
      description: 'The Consultant was unblocked'
    }));
    dispatch(updateCurrentCounsellorInfo({ counsellor: worker }));
    dispatch(updateCounsellor({ counsellor: worker }));
    return worker;
  }
);

export const shareLinkCounsellor = createAsyncThunk(
  ECounsellorsThunks.ShareLinkCounsellor,
  async ({ counsellor_id }: { counsellor_id: number }, { dispatch }) => {
    const { message, error } = await CounsellorsService.shareLinkCounsellor(counsellor_id);
    if (!message) {
      dispatch(openNotification({
        type: 'error',
        description: error ?? 'Couldn\'t send the App Link to consultant, try again'
      }));
      return false;
    }
    dispatch(openNotification({
      type: 'success',
      description: 'App Link was successfully send to Consultant`s Email'
    }));
    return true;
  }
);

export const resendInvitationCounsellor = createAsyncThunk(
  ECounsellorsThunks.ResendInvitationCounsellor,
  async ({ counsellor_id }: { counsellor_id: number }, { dispatch }) => {
    const { message, error } = await CounsellorsService.resendInvitationCounsellor(counsellor_id);
    if (!message) {
      dispatch(openNotification({
        type: 'error',
        description: error ?? 'Couldn\'t send the Invitation Email to the consultant, try again'
      }));
      return false;
    }
    dispatch(openNotification({
      type: 'success',
      description: 'Invitation was successfully send to Consultant`s Email'
    }));
    return true;
  }
);

export const archiveCounsellor = createAsyncThunk(
  ECounsellorsThunks.ArchiveCounsellor,
  async ({ counsellor_id }: { counsellor_id: number }, { dispatch }) => {
    const { expert, error } = await CounsellorsService.archiveCounsellor(counsellor_id);
    if (!expert) {
      dispatch(openNotification({
        type: 'error',
        description: error ?? 'The Consultant could not be archived, try again'
      }));
      return false;
    }
    dispatch(openNotification({
      type: 'success',
      description: 'The Consultant was archived'
    }));
    return expert;
  }
);

export const saveCounsellorsFilters = createAsyncThunk(
  ECounsellorsThunks.SaveCounsellorsFilters,
  async (filters: ICounsellorsFilters, { dispatch }) => {
    dispatch(CounsellorsSliceActions.setFilters(filters));
    return dispatch(loadCounsellors({ filters }));
  }
);

export const clearCounsellors = createAsyncThunk(
  ECounsellorsThunks.ClearCounsellors,
  async (_, { dispatch }) => {
    return dispatch(CounsellorsSliceActions.clearCounsellors());
  }
);
